import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	ApolloQueryError,
	AudioCoverageAreaUpdateInput,
	CombiningSubscriptionManager,
	DeviceResetScope,
	mapGQLNodeQueryOrThrow,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import {
	handleMutationResult,
	mapCoverageAreaTypeToSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { CoverageAreaType } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { tapOnce, noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import {
	CoverageAreaEnableConfigs,
	CoverageAudioCoverageAreaApiService
} from '../api/coverage-audio-coverage-area-api.service';
import { CoverageAudioCoverageArea, CoverageAreaRect } from '../api/models';

import {
	ActivateAudioCoverageAreaMutationGQL,
	CoverageAudioCoverageAreaSubscriptionGQL,
	DeactivateAudioCoverageAreaMutationGQL,
	GetCoverageAudioCoverageAreaQueryGQL,
	UpdateAudioCoverageAreaMutationGQL,
	UpdateAudioCoverageAreaPositionMutationGQL
} from './graphql/generated/device-coverage-audio-area.generated';
import { ResetDeviceMutationGQL } from './graphql/generated/device-coverage-device.generated';
import { mapCoverageAudioCoverageAreaFromSysApi, SysApiCoverageAreaRectMapper } from './mappers';

@Injectable()
export class SysApiCoverageAudioCoverageAreaApiService implements CoverageAudioCoverageAreaApiService {
	public readonly audioCoverageAreaGqlSubscriptions: CombiningSubscriptionManager;

	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		subscriptionManagerService: SubscriptionManagerService,
		coverageAudioCoverageAreaSubscriptionGQL: CoverageAudioCoverageAreaSubscriptionGQL,
		private readonly getCoverageAudioCoverageAreaQueryGQL: GetCoverageAudioCoverageAreaQueryGQL,
		private readonly updateAudioCoverageAreaMutationGQL: UpdateAudioCoverageAreaMutationGQL,
		private readonly activateAudioCoverageAreaMutationGQL: ActivateAudioCoverageAreaMutationGQL,
		private readonly deactivateAudioCoverageAreaMutationGQL: DeactivateAudioCoverageAreaMutationGQL,
		private readonly updateAudioCoverageAreaPositionMutationGQL: UpdateAudioCoverageAreaPositionMutationGQL,
		private readonly resetDeviceMutationGql: ResetDeviceMutationGQL
	) {
		this.logger = logger.createScopedLogger('SysApiCoverageAudioCoverageAreaApiService');

		this.audioCoverageAreaGqlSubscriptions = new CombiningSubscriptionManager(
			{
				name: 'SysApiCoverageApiService:AudioCoverageArea',
				changeTypes: [
					NodeChangeType.AudioCoverageAreaGain,
					NodeChangeType.AudioCoverageAreaMute,
					NodeChangeType.AudioCoverageAreaConfiguration
				],
				subscriptionGql: coverageAudioCoverageAreaSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getCoverageArea$(coverageAreaId: string): Observable<CoverageAudioCoverageArea> {
		const queryId = uuidv4();
		this.logger.trace('getCoverageArea$()', 'Querying for coverage area', { queryId, coverageAreaId });

		return this.getCoverageAudioCoverageAreaQueryGQL
			.watch({ variables: { id: coverageAreaId }, errorPolicy: 'all' })
			.valueChanges.pipe(
				mapGQLNodeQueryOrThrow('isCoverageAreaNode'),
				map((fragment) => {
					const coverageArea = mapCoverageAudioCoverageAreaFromSysApi(fragment);
					this.logger.trace('getCoverageArea$()', 'Query updated', { queryId, coverageArea });
					return coverageArea;
				}),
				tapOnce((area) => this.audioCoverageAreaGqlSubscriptions.subscribe(queryId, area.id)),
				noMoreSubscribers(() => this.audioCoverageAreaGqlSubscriptions.unsubscribeQuery(queryId)),
				catchError((error) => {
					this.logger.error('getCoverageArea$()', 'Query failed', { queryId, coverageAreaId, error });
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public addCoverageAreas(
		type: CoverageAreaType,
		coverageAreaEnableConfigs: CoverageAreaEnableConfigs[]
	): Observable<void> {
		this.logger.trace('addCoverageAreas()', 'Start', { type, coverageAreaEnableConfigs });

		return forkJoin(
			coverageAreaEnableConfigs.map(({ coverageAreaId, rect }) =>
				this.activateAudioCoverageAreaMutationGQL
					.mutate({
						variables: {
							id: coverageAreaId,
							type: mapCoverageAreaTypeToSysApi(type),
							coordinates: SysApiCoverageAreaRectMapper.toCoverageAreaCoordinates(rect),
							injectKeyring: '//supplied-by-keyring-injector'
						}
					})
					.pipe(handleMutationResult('activateAudioCoverageArea', this.logger, 'addCoverageAreas', {}))
			)
		).pipe(map(() => undefined));
	}

	public removeCoverageAreas(coverageAreaIds: string[]): Observable<void> {
		this.logger.trace('removeCoverageAreas()', 'Removing coverage areas', { coverageAreaIds });

		const removal = coverageAreaIds.map((coverageAreaId) =>
			this.deactivateAudioCoverageAreaMutationGQL
				.mutate({
					variables: { coverageAreaId, injectKeyring: '//supplied-by-keyring-injector' }
				})
				.pipe(
					map((mutation) => {
						if (mutation.error || mutation.data?.deactivateAudioCoverageArea.error) {
							throw (
								mutation.error ?? new ApolloQueryError(mutation.data?.deactivateAudioCoverageArea.error)
							);
						}

						return;
					}),
					catchError((error) => {
						this.logger.error('removeCoverageAreas()', 'Failed to remove coverage area', {
							coverageAreaId,
							error
						});

						return throwError(() => new ApolloQueryError(error));
					})
				)
		);

		return forkJoin(removal).pipe(map(() => undefined));
	}

	public setCoverageAreasMute(coverageAreaIds: string[], muted: boolean): Observable<void> {
		return this.updateCoverageAreas(coverageAreaIds.map((id) => ({ id, features: { mute: { muted } } })));
	}

	public setCoverageAreaGain(coverageAreaId: string, gain: number): Observable<void> {
		return this.updateCoverageAreas([{ id: coverageAreaId, features: { gain: { gain } } }]);
	}

	public setCoverageAreaRect(coverageAreaId: string, area: CoverageAreaRect): Observable<void> {
		const coordinates = SysApiCoverageAreaRectMapper.toCoverageAreaCoordinates(area);
		this.logger.trace('setCoverageAreaRect()', 'Setting coverage area rect', { coverageAreaId, area, coordinates });
		return this.updateAudioCoverageAreaPositionMutationGQL
			.mutate({
				variables: {
					id: coverageAreaId,
					coordinates,
					injectKeyring: '//supplied-by-keyring-injector'
				}
			})
			.pipe(
				map((mutation) => {
					this.logger.trace('setCoverageAreaRect()', 'Updated coverage area rect', { mutation });
					return undefined;
				}),
				catchError((error) => {
					this.logger.error('setCoverageAreaRect()', 'Failed to set coverage area rect', {
						coverageAreaId,
						area,
						coordinates,
						error
					});

					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public resetCoverageAreas(deviceId: string): Observable<void> {
		this.logger.trace('resetCoverageAreas()', 'Reseting coverage areas', { deviceId });

		return this.resetDeviceMutationGql
			.mutate({
				variables: {
					id: deviceId,
					scope: DeviceResetScope.CoverageAreas,
					injectKeyring: '//supplied-by-keyring-injector'
				}
			})
			.pipe(
				map((mutationResult) => {
					if (mutationResult.error) {
						throw mutationResult.error;
					}

					this.logger.trace('resetCoverageAreas()', 'Coverage areas reset', { mutationResult });
					return undefined;
				}),
				catchError((error: Error) => {
					this.logger.error(
						'resetCoverageAreas()',
						'Failed to reset coverage areas due to unexpected error',
						{ error }
					);
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public resetChannels(deviceId: string): Observable<void> {
		this.logger.trace('resetChannels()', 'Reseting channels', { deviceId });

		return this.resetDeviceMutationGql
			.mutate({
				variables: {
					id: deviceId,
					scope: DeviceResetScope.AllLobes,
					injectKeyring: '//supplied-by-keyring-injector'
				}
			})
			.pipe(
				map((mutationResult) => {
					if (mutationResult.error) {
						throw mutationResult.error;
					}

					this.logger.trace('resetChannels()', 'Channels reset', { mutationResult });
					return undefined;
				}),
				catchError((error: Error) => {
					this.logger.error('resetChannels()', 'Failed to reset channels due to unexpected error', { error });
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	private updateCoverageAreas(updates: AudioCoverageAreaUpdateInput[]): Observable<void> {
		this.logger.trace('updateCoverageAreas()', 'Updating coverage areas', { updates });
		return this.updateAudioCoverageAreaMutationGQL
			.mutate({
				variables: {
					updates: updates.map((update) => ({ audioCoverageArea: update })),
					injectKeyring: '//supplied-by-keyring-injector'
				}
			})
			.pipe(
				map((mutation) => {
					if (mutation.error) {
						throw mutation.error;
					}

					this.logger.trace('updateCoverageAreas()', 'Updated coverage areas', { updates });
					return undefined;
				}),
				catchError((error) => {
					this.logger.error('updateCoverageAreas()', 'Failed to update coverage areas', { updates, error });
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}
}
